import { useEffect, useRef } from 'react';
import { InlineFeedback } from '@/components/ui/inline-feedback';
import { useInlineFeedbackScope } from '@/hooks/useInlineFeedbackScope';
import { subscribeSessionEvents } from '@/lib/auth/sessionCoordinator';
import { useOptionalPublicBranding } from '@/contexts/PublicBrandingContext';
import { useThemeQueryClient } from '@/hooks/useThemeQuery';
import { QUERY_KEYS } from '@/lib/queryKeys';

export const AuthSessionCoordinatorBridge: React.FC = () => {
  const { feedback, scope } = useInlineFeedbackScope(5000);
  const brandingCtx = useOptionalPublicBranding();
  const themeQueryClient = useThemeQueryClient();
  const lastOfflineToastAtRef = useRef(0);

  useEffect(() => {
    const unsub = subscribeSessionEvents((event) => {
      if (event.type === 'NETWORK_ERROR' || event.type === 'OFFLINE_ENTER') {
        const now = Date.now();
        if (now - lastOfflineToastAtRef.current < 2000) return;
        lastOfflineToastAtRef.current = now;
        scope.emit('warning', 'Mất kết nối máy chủ. Vui lòng kiểm tra mạng.', 5000);
        return;
      }
      if (event.type === 'SERVER_ERROR') {
        scope.emit('warning', 'Hệ thống đang gián đoạn tạm thời. Vui lòng thử lại.', 5000);
        return;
      }
      if (event.type === 'BRANDING_REFRESHED') {
        brandingCtx?.refresh?.().catch(() => {
          // ignore
        });
        themeQueryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings.theme });
        return;
      }
      if (event.type === 'OFFLINE_EXIT') {
        scope.emit('success', 'Đã khôi phục kết nối.', 3500);
        return;
      }
      if (event.type === 'RECOVERED' || event.type === 'LOGIN_SUCCESS') {
        brandingCtx?.refresh?.().catch(() => {
          // ignore
        });
        themeQueryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings.theme });
      }
    });

    const onOnline = () => {
      scope.clear();
      brandingCtx?.refresh?.().catch(() => {
        // ignore
      });
      themeQueryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings.theme });
    };

    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      unsub();
    };
  }, [scope, brandingCtx, themeQueryClient]);

  return (
    <div className="px-4 pt-3 lg:px-6">
      <InlineFeedback
        type={feedback.type}
        message={feedback.message}
        show={feedback.show}
        onClose={scope.clear}
      />
    </div>
  );
};

